import { createContext, useCallback, useContext, useEffect, useMemo, useState, type PropsWithChildren } from "react"
import { ThemeProvider } from "@mui/material/styles"
import { Box } from "@mui/material"
import { createAppTheme, type ThemeMode } from "shared/theme"

const STORAGE_KEY = "oxy-dtvm:portal-theme-mode"

interface ThemeModeContextValue {
  mode: ThemeMode
  toggleMode: () => void
  setMode: (mode: ThemeMode) => void
}

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined)

function readInitialMode(): ThemeMode {
  if (typeof window === "undefined") {
    return "light"
  }

  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored === "dark" || stored === "light") {
    return stored
  }

  return "light"
}

export function ThemeModeProvider({ children }: PropsWithChildren) {
  const [mode, setModeState] = useState<ThemeMode>(readInitialMode)

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    window.localStorage.setItem(STORAGE_KEY, mode)
  }, [mode])

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next)
  }, [])

  const toggleMode = useCallback(() => {
    setModeState((prev) => (prev === "dark" ? "light" : "dark"))
  }, [])

  const theme = useMemo(() => createAppTheme(mode), [mode])

  const value = useMemo<ThemeModeContextValue>(
    () => ({ mode, toggleMode, setMode }),
    [mode, toggleMode, setMode],
  )

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <Box
          className={mode === "dark" ? "oxy-dark" : undefined}
          sx={{
            minHeight: "100vh",
            bgcolor: "var(--oxy-page-bg)",
            color: "var(--oxy-text-primary)",
          }}
        >
          {children}
        </Box>
      </ThemeProvider>
    </ThemeModeContext.Provider>
  )
}

const fallbackThemeMode: ThemeModeContextValue = {
  mode: "light",
  toggleMode: () => {},
  setMode: () => {},
}

export function useThemeMode(): ThemeModeContextValue {
  return useContext(ThemeModeContext) ?? fallbackThemeMode
}
